import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import styles from "./styles.module.scss";
import {
  acceptSettlementRequest,
  getSettlementRequests,
  rejectSettlementRequest,
} from "../../../../setup/api/services/contract";
import {
  ISettlementRequest,
  ESenderRole,
} from "../../../../setup/api/services/contract/models";
import { AuthConfig } from "../../../../setup/context/config/authConfig";
import Spinner from "../../../../components/common/Spinner";
import PrimaryButton from "../../../../components/common/PrimaryButton";
import OutlinedButton from "../../../../components/common/OutlinedButton";

const PENDING_STATUSES = ["PENDING_OWNER_APPROVAL"];
const ACCEPTED_STATUSES = ["ACCEPTED", "APPROVED", "EXECUTED", "COMPLETED"];
const REJECTED_STATUSES = ["REJECTED", "CANCELLED", "DENIED"];

interface SettlementRequestsProps {
  complexContractId: string;
}

const SettlementRequests = ({ complexContractId }: SettlementRequestsProps) => {
  const { t } = useTranslation();
  const [requests, setRequests] = useState<ISettlementRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoadingId, setActionLoadingId] = useState<string | null>(null);

  const userInfo = AuthConfig.getUserInfo();
  const userId = userInfo?.userIdentification || "";
  const actorRole = ESenderRole.CLIENT_ADMIN;

  const fetch = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getSettlementRequests(complexContractId);
      const map = res?.data ?? {};
      const flat = Object.entries(map).flatMap(([sourceContractId, arr]) =>
        Array.isArray(arr)
          ? arr.map((req) => ({ ...req, sourceContractId }))
          : []
      );
      setRequests(flat);
    } catch {
      setRequests([]);
    } finally {
      setLoading(false);
    }
  }, [complexContractId]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  const handleAccept = async (
    sourceContractId: string,
    settlementRequestId: string
  ) => {
    try {
      setActionLoadingId(settlementRequestId);
      await acceptSettlementRequest(
        sourceContractId,
        settlementRequestId,
        userId,
        actorRole
      );
      await fetch();
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleReject = async (
    sourceContractId: string,
    settlementRequestId: string
  ) => {
    try {
      setActionLoadingId(settlementRequestId);
      await rejectSettlementRequest(
        sourceContractId,
        settlementRequestId,
        userId,
        actorRole
      );
      await fetch();
    } finally {
      setActionLoadingId(null);
    }
  };

  if (loading) {
    return (
      <div className={styles.loadingRow}>
        <Spinner />
      </div>
    );
  }

  if (requests.length === 0) return null;

  return (
    <div className={styles.container}>
      <h2 className={styles.sectionTitle}>{t("lbl_settlement_requests")}</h2>
      <div className={styles.list}>
        {requests?.map((req) => {
          const isAccepted = ACCEPTED_STATUSES.includes(req.status);
          const isRejected = REJECTED_STATUSES.includes(req.status);
          const isPending = PENDING_STATUSES.includes(req.status);
          const isActing = actionLoadingId === req.id;

          const formattedDate = req.createdAt
            ? new Date(req.createdAt).toLocaleDateString("ar-EG", {
                day: "numeric",
                month: "long",
                year: "numeric",
              })
            : "";

          return (
            <div key={req.id} className={styles.item}>
              <div className={styles.itemRight}>
                <p className={styles.itemTitle}>{req.contractTitle}</p>
                <p className={styles.itemDate}>{formattedDate}</p>
              </div>
              <div className={styles.itemLeft}>
                <div className={styles.amountRow}>
                  <span className={styles.amount}>
                    {req.amount?.toLocaleString()}
                  </span>
                  <span className={styles.currency}>
                    {t("lbl_saudi_currency")}
                  </span>
                </div>
                {isAccepted && (
                  <span className={styles.badgeAccepted}>
                    {t("lbl_settlement_accepted")}
                  </span>
                )}
                {isRejected && (
                  <span className={styles.badgeRejected}>
                    {t("lbl_settlement_rejected")}
                  </span>
                )}
                {isPending && (
                  <div className={styles.actions}>
                    <PrimaryButton
                      disabled={isActing}
                      onClick={() =>
                        handleAccept(req.sourceContractId ?? "", req.id)
                      }
                      style={{
                        height: "32px",
                        fontSize: "13px",
                        minWidth: "80px",
                        background: "#0D44AD",
                      }}>
                      {isActing ? <Spinner /> : t("lbl_accept")}
                    </PrimaryButton>
                    <OutlinedButton
                      disabled={isActing}
                      onClick={() =>
                        handleReject(req.sourceContractId ?? "", req.id)
                      }
                      style={{
                        height: "32px",
                        fontSize: "13px",
                        minWidth: "80px",
                        color: "#eb4c43",
                        borderColor: "#eb4c43",
                      }}>
                      {isActing ? <Spinner /> : t("lbl_reject")}
                    </OutlinedButton>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default SettlementRequests;
